<template>
  <div class="claim__passenger claim__passenger--legal">
    <div
      class="text"
      v-if="representativeNum === 1"
    >{{ getUiString(['claim_details', 'details', 'passengers', 'lr_add_description']) }}</div>
    <h4 class="title--s">{{ label }}</h4>
    <div class="form__group" :class="[getTouchedClass,getExternalClass]">
      <lennoc-select
        :id="id"
        :label="selectLabel"
        :disabled="isFieldDisabled"
        :items="legalPersons"
        :value="selectedPerson"
        @input="setSelectedPerson"
      >
        <slot />
      </lennoc-select>
    </div>
    <div v-if="!!lpDisclaimer" class="text">{{ lpDisclaimer }}</div>
    <div class="claim__passenger--representative" v-if="showPersonData">
      <div class="row">
        <div class="cell">
          <div class="label">{{ getUiString(['form_fields', 'BirthDate']) }}</div>
          <div class="value">{{ formatDate(selectedPerson.BirthDate) }}</div>
        </div>
        <div class="cell" v-if="IsBirthPlaceRequired">
          <div class="label" >{{ getUiString(['form_fields', 'BirthPlace']) }}</div>
          <div class="value">{{ selectedPerson.BirthPlace }}</div>
        </div>
      </div>
      <div class="row">
        <div class="cell">
          <div class="label">{{ getUiString(['form_fields', 'City']) }}</div>
          <div class="value">{{ selectedPerson.City }}</div>
        </div>
        <div class="cell">
          <div class="label">{{ getUiString(['form_fields', 'EMailAddress']) }}</div>
          <div class="value">{{ selectedPerson.EMailAddress }}</div>
        </div>
      </div>
    </div>
    <div v-if="askingForReason" class="form__row">
      <div class="form__input">
        <textarea
        v-model="otherRemarks"
        @blur="setSelectedPerson({Code:'reason'})"
        name="reason"
        id="reason"
        cols="60"
        rows="10"></textarea>
      </div>
    </div>
    <lennoc-modal domClass="modal--slim" v-if="addingNonPassenger">
      <add-non-passenger
        v-if="addingNonPassenger"
        :IsBirthPlaceRequired="IsBirthPlaceRequired"
        @end-edit-person="endEditPerson"
        @cancel-edit-person="endEditPerson(false)"
        @edit-person-error="editPersonError"
      />
    </lennoc-modal>
  </div>
</template>

<script>
import day from 'dayjs'
import AddNonPassenger from '@/components/person/AddNonPassenger.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'ClaimLegalRepresentative',
  components: {
    AddNonPassenger
  },
  props: {
    id: {
      type: String,
      required: true
    },
    value: {
      type: Object,
      required: false
    },
    label: {
      type: String,
      required: false,
      default () {
        return ''
      }
    },
    domclass: {
      type: String,
      required: false,
      default () {
        return ''
      }
    },
    disabled: {
      type: Boolean,
      required: false,
      default () {
        return false
      }
    },
    canComplete: {
      type: Boolean,
      required: false,
      default () {
        return true
      }
    },
    canModify: {
      type: Boolean,
      required: false,
      default () {
        return true
      }
    },
    items: {
      type: Array,
      required: false,
      default () {
        return []
      }
    },
    IsBirthPlaceRequired: {
      type: Boolean,
      required: false
    }
  },
  data () {
    return {
      selectedPerson: null,
      addingNonPassenger: false,
      askingForReason: false,
      additionalLegalPersons: [],
      otherRemarks: ''
    }
  },
  beforeMount () {
    this.additionalLegalPersons.push({
      Name: this.getUiString(['claim_details', 'details', 'passengers', 'lr_so_else']),
      Code: 'new',
      Id: null
    })
    if (this.representativeNum === 2) {
      this.additionalLegalPersons.push({
        Name: this.getUiString(['claim_details', 'details', 'passengers', 'lr_not_applicable']),
        Code: 'Other',
        RemarksOther: '',
        Id: null
      })
    }

    if (!this.value) {
      this.selectedPerson = null
    } else if (this.value.LegalRepresentativeLinkType === 'Other') {
      this.selectedPerson = this.legalPersons.find(el => el.Code === 'Other')
      this.selectedPerson.RemarksOther = this.value.RemarksOther
    } else {
      const selPerson = this.legalPersons.find(el => el.Code === this.value.PersonId)
      this.selectedPerson = selPerson || null
    }
  },
  computed: {
    ...mapGetters({
      getUiString: 'uistrings/getUiString'
    }),
    showPersonData () {
      return this.selectedPerson && this.selectedPerson.Id
    },
    lpDisclaimer () {
      if (!this.selectedPerson) {
        return this.getUiString(['claim_details', 'details', 'passengers', 'lr_add_docs'])
      }
      if (this.askingForReason) {
        return this.getUiString(['claim_details', 'details', 'passengers', 'ask_reason_disclaimer'])
      }
      return ''
    },
    isFieldDisabled () {
      if (this.canModify) {
        return false
      }
      if (this.canComplete) {
        return !!this.selectedPerson
      }
      return true
    },
    legalPersons () {
      const legalPersons = this.items.map(lp => {
        return {
          Name: `${lp.FirstName} ${lp.MiddleName ? lp.MiddleName + ' ' : ''}${lp.LastName}`,
          Code: lp.Id,
          ...lp
        }
      })
      return [...legalPersons, ...this.additionalLegalPersons]
    },
    selectLabel () {
      return this.getUiString(['claim_details', 'details', 'passengers', 'lr_choose']) + this.label
    },
    representativeNum () {
      return this.label[this.label.length - 1] === '1' ? 1 : 2
    }
  },
  methods: {
    setSelectedPerson (newPerson) {
      switch (newPerson.Code) {
        case 'new':
          this.addingNonPassenger = true
          this.askingForReason = false
          break
        case 'Other':
          this.selectedPerson = newPerson
          this.addingNonPassenger = false
          this.askingForReason = true
          this.otherRemarks = newPerson.RemarksOther
          this.$emit('input', {
            LegalRepresentativeLinkType: 'Other',
            AskingForReason: this.askingForReason,
            RemarksOther: newPerson.RemarksOther
          })
          break
        case 'reason':
          this.$emit('input', {
            LegalRepresentativeLinkType: 'Other',
            PersonId: null,
            RemarksOther: this.otherRemarks
          })
          break
        default:
          this.selectedPerson = newPerson
          this.askingForReason = false
          this.$emit('input', {
            LegalRepresentativeLinkType: this.selectedPerson.LegalRepresentativeLinkType,
            PersonId: this.selectedPerson.Id
          })
          break
      }
    },
    formatDate (val) {
      return day(val).format('DD-MM-YYYY')
    },
    getTouchedClass (member) {
      return this.account[member] !== '' ? 'form__group--touched' : ''
    },
    getExternalClass () {
      return this.domclass
    },
    endEditPerson (result) {
      if (result) {
        const newlyAddedPerson = { Code: result.Id, ...result }
        this.$nextTick(() => {
          this.setSelectedPerson(newlyAddedPerson)
        })
      } else {
        const oldSelectedPerson = this.selectedPerson
        this.selectedPerson = null
        this.$nextTick(() => {
          this.selectedPerson = oldSelectedPerson
        })
      }
      this.addingNonPassenger = false
    },
    editPersonError () {
      this.addingNonPassenger = false
    }
  }
}
</script>
